import { useCallback } from "react";
import type { CheckboxIndicatorState } from "@design-system/Checkbox/CheckboxIndicator";
import { usePickerChain } from "./PickerChainContext";
import { usePickerRoot } from "./PickerContext";
import type { PickerItemValue } from "./Picker.types";

export interface PickerCascadeRow {
    /** Checked when every enabled leaf below is selected, indeterminate when some are. */
    state: CheckboxIndicatorState;
    /** Selects every enabled leaf below the row, or removes them all when they are all selected. */
    toggle: () => void;
}

/**
 * The cascade behaviour of a parent row under `parentSelection="cascade"`:
 * `null` for rows that are not cascade parents. Works against the chain's
 * full selection so a flyout parent cascades into a level that was never
 * opened, and commits once through the top root.
 */
export const usePickerCascadeRow = (
    value: PickerItemValue | undefined,
): PickerCascadeRow | null => {
    const root = usePickerRoot();
    const chain = usePickerChain();
    const leaves =
        value === undefined ? undefined : root.cascadeDescendants?.get(value);
    // A level's root mirrors the chain's selection, so its Set answers for
    // both; commits still go through the chain to the top root.
    const { isSelected, selectedValues } = root;
    const commit = chain?.commitValues ?? root.setSelectedValues;
    const selectedLeafCount =
        leaves?.filter((leaf) => isSelected(leaf)).length ?? 0;
    const isEveryLeafSelected =
        leaves !== undefined &&
        leaves.length > 0 &&
        selectedLeafCount === leaves.length;

    const toggle = useCallback(() => {
        if (leaves === undefined || leaves.length === 0) {
            return;
        }
        const leafSet = new Set<PickerItemValue>(leaves);
        if (isEveryLeafSelected) {
            commit(selectedValues.filter((item) => !leafSet.has(item)));
            return;
        }
        commit([
            ...selectedValues,
            ...leaves.filter((leaf) => !isSelected(leaf)),
        ]);
    }, [leaves, isEveryLeafSelected, commit, selectedValues, isSelected]);

    if (leaves === undefined) {
        return null;
    }
    let state: CheckboxIndicatorState = "unchecked";
    if (isEveryLeafSelected) {
        state = "checked";
    } else if (selectedLeafCount > 0) {
        state = "indeterminate";
    }
    return { state, toggle };
};
